<script setup lang="ts">
/**
 * The marquee (rubber-band) rectangle of a selection drag, drawn in world
 * units inside `NxPlanCanvas`'s default slot. Its outline stays one crisp
 * line at every zoom level. `contain` draws it solid (selects what lies
 * completely inside), `intersect` dashed (selects what it touches) — the CAD
 * convention. State usually comes from `useNxPlanMarquee`.
 */
import type { NxPlanBounds } from './geometry';
import type { NxPlanMarqueeMode } from './hitTest';

const props = withDefaults(
  defineProps<{
    /** The rectangle in world units; nothing is drawn while `null`. */
    bounds: NxPlanBounds | null;
    mode?: NxPlanMarqueeMode;
  }>(),
  { mode: 'contain' }
);
</script>

<template>
  <rect
    v-if="props.bounds"
    class="nx-plan-marquee"
    :class="`is-${props.mode}`"
    :x="props.bounds.minX"
    :y="props.bounds.minY"
    :width="props.bounds.maxX - props.bounds.minX"
    :height="props.bounds.maxY - props.bounds.minY"
    vector-effect="non-scaling-stroke"
    aria-hidden="true"
    data-testid="nx-plan-marquee"
  />
</template>

<style scoped>
.nx-plan-marquee {
  fill: var(--nxt-brand-subtle-bg);
  fill-opacity: 0.45;
  stroke: var(--nxt-brand-bg);
  stroke-width: calc(var(--nxt-hairline) * 1.5);
  pointer-events: none;
}
.nx-plan-marquee.is-intersect {
  stroke-dasharray: calc(var(--nxt-hairline) * 5) calc(var(--nxt-hairline) * 3);
}
@media (forced-colors: active) {
  .nx-plan-marquee {
    fill: none;
    stroke: Highlight;
  }
}
</style>
